import { useContext, useEffect, useMemo, useState } from "react";
import {
  Timestamp,
  addDoc,
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { AuthContext } from "../AuthContext";

const REQUESTER_OPTIONS = ["pupil", "parent/carer", "staff", "other"];
const REQUEST_TYPES = ["access", "rectify", "erase", "restrict", "other"];
const METHOD_OPTIONS = ["email", "letter", "phone", "in-person", "other"];
const STATUS_OPTIONS = ["new", "in-progress", "fulfilled", "denied"];

const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const formatTimestamp = (value) => {
  if (!value) return "";
  if (typeof value.toDate === "function") return value.toDate().toLocaleString();
  if (value.seconds) return new Date(value.seconds * 1000).toLocaleString();
  return "";
};

export default function AdminSarLog() {
  const { isAdmin, loading: authLoading } = useContext(AuthContext);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const [subjectName, setSubjectName] = useState("");
  const [requesterType, setRequesterType] = useState(REQUESTER_OPTIONS[0]);
  const [requestType, setRequestType] = useState(REQUEST_TYPES[0]);
  const [method, setMethod] = useState(METHOD_OPTIONS[0]);
  const [receivedDate, setReceivedDate] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (authLoading || !isAdmin) {
      setLoading(false);
      return;
    }

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const q = query(collection(db, "sarRequests"), orderBy("receivedAt", "desc"));
        const snap = await getDocs(q);
        setItems(snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      } catch (err) {
        console.error(err);
        setError("Failed to load SAR log.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [authLoading, isAdmin]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (!subjectName.trim()) {
      setError("Subject name is required.");
      return;
    }

    setSaving(true);

    try {
      const receivedAtDate = receivedDate ? new Date(receivedDate) : new Date();
      const dueBy = addDays(receivedAtDate, 30).toISOString().slice(0, 10);

      const payload = {
        subjectName: subjectName.trim(),
        requesterType,
        requestType,
        method,
        status: "new",
        receivedAt: receivedDate ? Timestamp.fromDate(receivedAtDate) : serverTimestamp(),
        dueBy,
        notes: notes.trim(),
      };

      const docRef = await addDoc(collection(db, "sarRequests"), payload);
      setItems((prev) => [{ id: docRef.id, ...payload }, ...prev]);

      setSubjectName("");
      setRequesterType(REQUESTER_OPTIONS[0]);
      setRequestType(REQUEST_TYPES[0]);
      setMethod(METHOD_OPTIONS[0]);
      setReceivedDate("");
      setNotes("");
    } catch (err) {
      console.error(err);
      setError("Failed to log SAR request.");
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (itemId, status) => {
    setError("");
    try {
      await updateDoc(doc(db, "sarRequests", itemId), { status });
      setItems((prev) => prev.map((item) => (item.id === itemId ? { ...item, status } : item)));
    } catch (err) {
      console.error(err);
      setError("Failed to update SAR status.");
    }
  };

  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      const aTime = a.receivedAt?.toMillis ? a.receivedAt.toMillis() : 0;
      const bTime = b.receivedAt?.toMillis ? b.receivedAt.toMillis() : 0;
      return bTime - aTime;
    });
  }, [items]);

  return (
    <div style={{ padding: "1.5rem" }}>
      <h1>SAR Log</h1>
      {authLoading ? (
        <p>Loading...</p>
      ) : !isAdmin ? (
        <p>You do not have access to this page.</p>
      ) : (
        <>
          <section style={{ marginBottom: "1.5rem" }}>
            <h2>Log a request</h2>
            {error && <p style={{ color: "#b00020" }}>{error}</p>}
            <form onSubmit={handleSubmit} style={{ display: "grid", gap: "0.75rem", maxWidth: "520px" }}>
              <label style={{ display: "grid", gap: "0.35rem" }}>
                Subject name
                <input
                  type="text"
                  value={subjectName}
                  onChange={(event) => setSubjectName(event.target.value)}
                  required
                />
              </label>
              <label style={{ display: "grid", gap: "0.35rem" }}>
                Requester type
                <select value={requesterType} onChange={(event) => setRequesterType(event.target.value)}>
                  {REQUESTER_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ display: "grid", gap: "0.35rem" }}>
                Request type
                <select value={requestType} onChange={(event) => setRequestType(event.target.value)}>
                  {REQUEST_TYPES.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ display: "grid", gap: "0.35rem" }}>
                Intake method
                <select value={method} onChange={(event) => setMethod(event.target.value)}>
                  {METHOD_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ display: "grid", gap: "0.35rem" }}>
                Received date
                <input
                  type="date"
                  value={receivedDate}
                  onChange={(event) => setReceivedDate(event.target.value)}
                />
              </label>
              <label style={{ display: "grid", gap: "0.35rem" }}>
                Notes
                <textarea
                  rows={3}
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                />
              </label>
              <button type="submit" disabled={saving}>
                {saving ? "Saving..." : "Add request"}
              </button>
            </form>
          </section>

          <section>
            <h2>Requests</h2>
            {loading ? (
              <p>Loading SARs...</p>
            ) : sortedItems.length === 0 ? (
              <p>No SARs logged yet.</p>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Subject</th>
                    <th style={thStyle}>Requester</th>
                    <th style={thStyle}>Type</th>
                    <th style={thStyle}>Method</th>
                    <th style={thStyle}>Received</th>
                    <th style={thStyle}>Due by</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedItems.map((item) => (
                    <tr key={item.id}>
                      <td style={tdStyle}>{item.subjectName || "–"}</td>
                      <td style={tdStyle}>{item.requesterType || "–"}</td>
                      <td style={tdStyle}>{item.requestType || "–"}</td>
                      <td style={tdStyle}>{item.method || "–"}</td>
                      <td style={tdStyle}>{formatTimestamp(item.receivedAt) || "–"}</td>
                      <td style={tdStyle}>{item.dueBy || "–"}</td>
                      <td style={tdStyle}>
                        <select
                          value={item.status || "new"}
                          onChange={(event) => updateStatus(item.id, event.target.value)}
                        >
                          {STATUS_OPTIONS.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={tdStyle}>{item.notes || "–"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </div>
  );
}

const thStyle = {
  textAlign: "left",
  borderBottom: "1px solid #ddd",
  padding: "0.5rem",
};

const tdStyle = {
  borderBottom: "1px solid #eee",
  padding: "0.5rem",
  verticalAlign: "top",
};
